// Settings → Team: hourly labour cost per person (COST-02). Seen with
// costing.view; edited with costing.manage. Never shown to Crew.
import '@/components/JobCosting/jobCosting.css'
import { useState } from 'react'
import { useApiData } from '@/hooks/useApi'
import { useCostingPermission } from '@/components/JobCosting/fixturePermissions'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { formatMoney } from '@/lib/format'
import { toast } from '@/lib/toast'
import { costingApi, designMode, type LaborRateRow } from '@/components/JobCosting/api'

export function LaborRatesSection() {
  const canView = useCostingPermission('costing.view')
  if (!canView) return null
  return <LaborRatesInner key={String(designMode())} />
}

function LaborRatesInner() {
  const canManage = useCostingPermission('costing.manage')
  const q = useApiData<LaborRateRow[]>(() => costingApi.laborRates(), [])
  return (
    <section aria-label="Labour cost rates" className="job-costing mt-6">
      <h3 className="text-base font-semibold">Labour cost rates</h3>
      {designMode() && <p>Demo — rates are temporary.</p>}
      <p className="text-xs" style={{ color: 'var(--midnight-text3)', marginBottom: 8 }}>
        What an hour of each person costs you. Used for job costing; customers and Crew never see it.
      </p>
      {q.error ? <p role="alert" className="text-sm">{q.error}</p> : (
        <ul style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(q.data ?? []).map((p) => <RateRow key={p.id} person={p} canManage={canManage} onSaved={q.refetch} />)}
        </ul>
      )}
    </section>
  )
}

function RateRow({ person, canManage, onSaved }: { person: LaborRateRow; canManage: boolean; onSaved: () => void }) {
  const demo = designMode()
  const name = `${person.firstName} ${person.lastName}`.trim() || person.email
  const [value, setValue] = useState(person.laborCostRate == null ? '' : String(person.laborCostRate))
  const [error, setError] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)
  const dirty = value !== (person.laborCostRate == null ? '' : String(person.laborCostRate))

  async function save() {
    const v = value.trim() === '' ? null : Number(value)
    if (v != null && (!Number.isFinite(v) || v < 0 || v > 10_000)) { setError('Enter a rate between $0 and $10,000 an hour.'); return }
    setError(undefined); setSaving(true)
    try { await costingApi.setLaborRate(person.id, v); toast.success(demo ? `Demo rate updated temporarily for ${name}` : `Rate saved for ${name}`); onSaved() }
    catch (err) { toast.error(err, 'save the rate') }
    finally { setSaving(false) }
  }

  return (
    <li className="labour-rate-row">
      <span className="text-sm" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{name}</span>
      {canManage ? (
        <>
          <Input label={`Hourly cost for ${name}`} inputMode="decimal" value={value} error={error} onChange={(e) => setValue(e.target.value)} />
          <Button size="sm" variant="secondary" needsNetwork disabled={!dirty} loading={saving} onClick={save}>Save</Button>
        </>
      ) : (
        <span className="text-sm">{person.laborCostRate == null ? 'Not set' : `${formatMoney(person.laborCostRate)}/h`}</span>
      )}
    </li>
  )
}
